import { Connection, CloseConnectionOutcome } from '../../../types/electron';
import { ActiveConnection, ConnectionStatus } from '../models/connectionTypes';

/**
 * Full status envelope returned by checkConnectionStatus.
 * Preserves tunnelState alongside the connection state.
 */
export type ConnectionStatusEnvelope = {
  status: ConnectionStatus;
  tunnelState?: 'up' | 'down' | 'connecting' | 'error';
};

/**
 * Opens a connection to MS SQL Server via Electron
 *
 * Note: The actual connection is established in the Electron main process
 * since we can't use Node.js modules directly in the renderer process
 */
/**
 * Monotonic per-renderer connect counter (review r4): the identity of a connect
 * ATTEMPT. `connectedAt` cannot serve as one — `Date` has millisecond
 * resolution, so two connects (a disconnect + reconnect, a forced in-place
 * Reconnect) can share a timestamp and look like the same generation to anything
 * that fences work on it.
 */
let connectGeneration = 0;
export const nextConnectionGeneration = (): number => (connectGeneration += 1);

export async function openConnection(
  connection: Connection,
  opts?: { force?: boolean; attemptToken?: string },
): Promise<ActiveConnection> {
  try {
    if (!window.electronAPI) {
      throw new Error('Electron API is not available');
    }

    // Send a request to the main process to open a connection.
    // `opts.force` asks the main process to rebuild pools/auth state.
    const result = await window.electronAPI.connectToDatabase(connection, opts);

    if (result.success) {
      // No status round trip here any more. Main only reports success once the
      // session is established (every manager validates it: a pool checkout
      // plus a probe, and the SQL Tools Service route awaits
      // `connection/complete`), so `checkConnectionStatus` was asking a
      // question already answered — and paying for it, since each engine's
      // `checkStatus` re-probes the server (`SELECT 1`, `print 1`, a full STS
      // `executeQuery`) on the connect path of EVERY connection.
      //
      // The one thing the status envelope carried that the connect result did
      // not — `tunnelState` — the connect handler now answers itself.
      return {
        id: connection.id,
        connection,
        status: 'connected',
        tunnelState: result.tunnelState,
        connectedAt: new Date(),
        generation: nextConnectionGeneration()
      };
    } else {
      return {
        id: connection.id,
        connection,
        status: 'error',
        error: result.error || 'Failed to connect to database',
        errorCode: result.errorCode
      };
    }
  } catch (error) {
    console.error('Error opening connection:', error);
    return {
      id: connection.id,
      connection,
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * What a teardown reported — the shared `CloseConnectionOutcome`
 * (`src/types/electron.d.ts`). `'superseded'` means "still WANTED, not
 * disconnected": a connect the user asked for arrived after this disconnect
 * did. Main answers it from two checks — one BEFORE the teardown, where it
 * stood down and the connection is still up, and one AFTER (11c r5 #1), where
 * the teardown DID run and the queued connect is about to dial. Either way the
 * renderer keeps the entry. Main used to answer a bare `true` there, and the UI
 * dutifully marked a live connection disconnected.
 */
export type { CloseConnectionOutcome };

/**
 * Closes an active SQL Server connection
 */
export async function closeConnection(connectionId: string): Promise<CloseConnectionOutcome> {
  try {
    if (!window.electronAPI) {
      throw new Error('Electron API is not available');
    }

    // Send a request to the main process to close the connection. The IPC
    // contract carries the third outcome itself (R11) — no cast.
    return await window.electronAPI.disconnectFromDatabase(connectionId);
  } catch (error) {
    console.error('Error closing connection:', error);
    return false;
  }
}

/**
 * Tells the main process whether an in-flight connect is still wanted.
 *
 * Withdrawing is distinct from `closeConnection`: it is scoped to one
 * attempt's token, so it closes a pool only while that pool is still the one
 * that attempt opened, and is a no-op once a newer attempt owns the
 * connection. Safe to call whether the attempt is dialing or has landed.
 */
export async function setConnectIntent(
  connectionId: string,
  attemptToken: string | undefined,
  wanted: boolean
): Promise<CloseConnectionOutcome> {
  try {
    if (!window.electronAPI?.setConnectIntent) return false;
    return await window.electronAPI.setConnectIntent(connectionId, attemptToken, wanted);
  } catch (error) {
    console.error('Error recording connect intent:', error);
    return false;
  }
}

/**
 * Checks the status of an active connection.
 * Returns the full status envelope including optional tunnelState.
 */
export async function checkConnectionStatus(connectionId: string): Promise<ConnectionStatusEnvelope> {
  try {
    if (!window.electronAPI) {
      return { status: 'disconnected' };
    }

    const result = await window.electronAPI.checkConnectionStatus(connectionId);
    return { status: result.state, tunnelState: result.tunnelState };
  } catch (error) {
    console.error('Error checking connection status:', error);
    return { status: 'error' };
  }
}
